import { useState, useRef } from 'react';
import { X, Check, Upload, RefreshCw, Loader2, User } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper to get full image URL
const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('/static/')) {
    return `${BACKEND_URL}${url}`;
  }
  return url;
};

const PORTRAIT_REGENERATION_LIMIT = 1;

const CharacterPortraitPicker = ({
  seriesId,
  character,
  isOpen,
  onClose,
  onPortraitChange,
}) => {
  // Initialize state from character data
  const initPortraits = () => {
    if (character.portraits && character.portraits.length > 0) {
      return character.portraits;
    }
    return character.portrait_url ? [character.portrait_url] : [];
  };

  const [state, setState] = useState('selecting'); // 'selecting' | 'regenerating' | 'uploading'
  const [portraits, setPortraits] = useState(initPortraits);
  const [activeIndex, setActiveIndex] = useState(character.active_portrait_index || 0);
  const [regenerationCount, setRegenerationCount] = useState(character.portrait_regeneration_count || 0);
  const fileInputRef = useRef(null);

  const regenerationsRemaining = PORTRAIT_REGENERATION_LIMIT - regenerationCount;
  const canRegenerate = regenerationsRemaining > 0;

  const handleSelectPortrait = async (index) => {
    if (index === activeIndex) return;

    try {
      const response = await axios.post(
        `${API}/v1/series/${seriesId}/characters/${character.id}/portrait/set`,
        { portrait_index: index },
        { withCredentials: true }
      );

      setActiveIndex(index);
      onPortraitChange(character.id, response.data.portrait_url, response.data.portraits, index);
      toast.success('Portrait updated');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to update portrait';
      toast.error(message);
    }
  };

  const handleRegenerate = async () => {
    if (!canRegenerate) return;

    setState('regenerating');

    try {
      const response = await axios.post(
        `${API}/v1/series/${seriesId}/characters/${character.id}/portrait/generate`,
        {},
        { withCredentials: true }
      );

      const newPortraits = response.data.portraits;
      const newActiveIndex = response.data.active_portrait_index;

      setPortraits(newPortraits);
      setActiveIndex(newActiveIndex);
      setRegenerationCount(response.data.portrait_regeneration_count);
      onPortraitChange(character.id, response.data.portrait_url, newPortraits, newActiveIndex);
      toast.success('New portrait generated');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to generate portrait';
      toast.error(message);
    } finally {
      setState('selecting');
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please use JPG, PNG, or WebP.');
      return;
    }

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 10MB.');
      return;
    }

    setState('uploading');

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(
        `${API}/v1/series/${seriesId}/characters/${character.id}/portrait/upload`,
        formData,
        {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' },
        }
      );

      const newPortraits = response.data.portraits;
      const newActiveIndex = response.data.active_portrait_index;

      setPortraits(newPortraits);
      setActiveIndex(newActiveIndex);
      onPortraitChange(character.id, response.data.portrait_url, newPortraits, newActiveIndex);
      toast.success('Portrait transformed and saved');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to upload portrait';
      toast.error(message);
    } finally {
      setState('selecting');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleClose = () => {
    if (state !== 'selecting') return;
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      style={{ background: 'rgba(0, 0, 0, 0.5)' }}
      onClick={handleClose}
    >
      <div
        className="rounded-2xl max-w-xl w-full max-h-[90vh] overflow-hidden flex flex-col"
        style={{
          background: 'var(--st-bg-surface)',
          border: '1px solid var(--st-border-light)',
          boxShadow: '0 12px 32px rgba(var(--st-shadow-color), 0.18)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-6 py-4 border-b"
          style={{ borderColor: 'var(--st-border-light)' }}
        >
          <div className="flex items-center gap-3">
            <User className="w-5 h-5" style={{ color: 'var(--st-primary)' }} />
            <div>
              <h2
                className="text-lg font-bold"
                style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}
              >
                {character.name}'s Portrait
              </h2>
              <span
                className="text-xs"
                style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}
              >
                {character.role}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span
              className={`text-sm px-2 py-0.5 rounded-full ${canRegenerate ? 'dark:bg-green-900/40' : 'dark:bg-red-900/40'}`}
              style={{
                fontFamily: 'Quicksand, sans-serif',
                color: canRegenerate ? '#16A34A' : '#DC2626',
                background: canRegenerate ? '#DCFCE7' : '#FEE2E2',
              }}
            >
              {regenerationsRemaining} regen left
            </span>
            <button
              onClick={handleClose}
              disabled={state !== 'selecting'}
              className="p-2 rounded-lg hover:bg-orange-50 dark:hover:bg-orange-900/20 transition-colors disabled:opacity-50"
            >
              <X className="w-5 h-5" style={{ color: 'var(--st-text-heading)' }} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {state === 'regenerating' && (
            <div className="flex flex-col items-center justify-center py-12 space-y-4">
              <Loader2
                className="w-12 h-12 animate-spin"
                style={{ color: 'var(--st-primary)' }}
              />
              <p
                className="text-lg font-semibold"
                style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-heading)' }}
              >
                Generating new portrait...
              </p>
              <p
                className="text-sm"
                style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}
              >
                This may take a moment
              </p>
            </div>
          )}

          {state === 'uploading' && (
            <div className="flex flex-col items-center justify-center py-12 space-y-4">
              <Loader2
                className="w-12 h-12 animate-spin"
                style={{ color: 'var(--st-primary)' }}
              />
              <p
                className="text-lg font-semibold"
                style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-heading)' }}
              >
                Transforming photo...
              </p>
              <p
                className="text-sm"
                style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}
              >
                Converting to {character.name}'s art style
              </p>
            </div>
          )}

          {state === 'selecting' && (
            <div className="space-y-4">
              {/* Portrait Grid - 2 columns for portraits */}
              <div className="grid grid-cols-2 gap-4">
                {portraits.map((portrait, index) => (
                  <button
                    key={index}
                    onClick={() => handleSelectPortrait(index)}
                    className="relative aspect-square rounded-xl overflow-hidden transition-all hover:scale-[1.02]"
                    style={{
                      border: index === activeIndex
                        ? '3px solid var(--st-primary)'
                        : '1px solid var(--st-border-light)',
                      boxShadow: index === activeIndex
                        ? '0 6px 20px rgba(var(--st-glow-color), 0.4)'
                        : '0 2px 8px rgba(var(--st-shadow-color), 0.08)',
                    }}
                  >
                    <img
                      src={getImageUrl(portrait) || ''}
                      alt={`Portrait ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                    {index === activeIndex && (
                      <div
                        className="absolute top-2 right-2 w-6 h-6 rounded-full flex items-center justify-center"
                        style={{ background: 'var(--st-primary)' }}
                      >
                        <Check className="w-4 h-4 text-white" />
                      </div>
                    )}
                    <div
                      className="absolute bottom-0 left-0 right-0 py-1 px-2 text-xs text-center"
                      style={{
                        background: 'var(--st-bg-surface)',
                        fontFamily: 'Quicksand, sans-serif',
                        color: 'var(--st-text-heading)',
                      }}
                    >
                      {index === 0 ? 'Original' : `Version ${index + 1}`}
                    </div>
                  </button>
                ))}

                {/* Generate new placeholder - only if can regenerate */}
                {canRegenerate && (
                  <button
                    onClick={handleRegenerate}
                    className="aspect-square rounded-xl flex flex-col items-center justify-center gap-2 transition-all hover:scale-[1.02]"
                    style={{
                      border: '2px dashed var(--st-border-light)',
                      background: 'var(--st-bg-muted)',
                    }}
                  >
                    <RefreshCw className="w-6 h-6" style={{ color: 'var(--st-primary)' }} />
                    <span
                      className="text-xs font-semibold"
                      style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-heading)' }}
                    >
                      Generate New
                    </span>
                  </button>
                )}

                {/* Upload custom placeholder */}
                <button
                  onClick={handleUploadClick}
                  className="aspect-square rounded-xl flex flex-col items-center justify-center gap-2 transition-all hover:scale-[1.02]"
                  style={{
                    border: '2px dashed var(--st-border-light)',
                    background: 'var(--st-bg-muted)',
                  }}
                >
                  <Upload className="w-6 h-6" style={{ color: 'var(--st-text-muted)' }} />
                  <span
                    className="text-xs font-semibold text-center px-2"
                    style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-heading)' }}
                  >
                    Upload Photo
                  </span>
                </button>
              </div>

              {/* Help text */}
              <p
                className="text-xs text-center"
                style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}
              >
                Click a portrait to use it, or upload a photo to transform into the series art style
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          className="flex items-center justify-between px-6 py-4 border-t"
          style={{ borderColor: 'var(--st-border-light)' }}
        >
          <span
            className="text-xs"
            style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}
          >
            {portraits.length} portrait{portraits.length !== 1 ? 's' : ''} saved
          </span>
          <button
            onClick={handleClose}
            disabled={state !== 'selecting'}
            className="px-6 py-2.5 rounded-xl font-semibold text-sm transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50"
            style={{
              background: 'linear-gradient(135deg, var(--st-gradient-primary-start) 0%, var(--st-gradient-primary-end) 100%)',
              color: '#FFF8F0',
              fontFamily: 'Quicksand, sans-serif'
            }}
          >
            Done
          </button>
        </div>

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          onChange={handleFileChange}
          className="hidden"
        />
      </div>
    </div>
  );
};

export default CharacterPortraitPicker;
